import React, { useState } from 'react';
import { UseMutationResult } from '@tanstack/react-query';
import { AccountAutosuggest } from '../AccountAutosuggest';
import type { FinancialAccount } from '@/models/FinancialAccount';

interface CreateTransactionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  parentId: number | null;
  filteredAccounts: FinancialAccount[];
  selectedRole?: { type: string; entityName?: string };
  createMutation: UseMutationResult<any, Error, any, unknown>;
}

export const CreateTransactionDialog: React.FC<CreateTransactionDialogProps> = ({
  isOpen,
  onClose,
  parentId,
  filteredAccounts,
  selectedRole,
  createMutation,
}) => {
  const [selectedFromAccount, setSelectedFromAccount] = useState<number | null>(null);
  const [selectedToAccount, setSelectedToAccount] = useState<number | null>(null);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    if (!selectedFromAccount || !selectedToAccount) {
      alert('Please select both From Account and To Account');
      return;
    }

    const data = {
      transaction_type: 'manual_adjustment' as const,
      transaction_date: new Date().toISOString(),
      amount: parseFloat(formData.get('amount') as string),
      currency: 'USD',
      status: 'pending' as const,
      provider: 'manual' as const,
      account_from_id: selectedFromAccount,
      account_to_id: selectedToAccount,
      description: formData.get('description') as string,
      parent_transaction_id: parentId,
    };
    createMutation.mutate(data);
  };

  const getHelpText = () => {
    if (selectedRole?.type === 'platform') {
      return 'Platform, Stripe, Management Company, or Association account';
    }
    if (selectedRole?.type === 'management_company') {
      return 'Platform, Stripe, or your associations';
    }
    if (selectedRole?.type === 'association') {
      return 'Platform, Stripe, or your association account';
    }
    return '';
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-2xl font-bold">
            {parentId ? `Add Child Transaction to #${parentId}` : 'Create New Transaction'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <AccountAutosuggest
              accounts={filteredAccounts}
              value={selectedFromAccount}
              onChange={setSelectedFromAccount}
              label="From Account *"
              placeholder="Search for account..."
            />
            <p className="text-xs text-gray-500 mt-1">{getHelpText()}</p>
          </div>

          <div>
            <AccountAutosuggest
              accounts={filteredAccounts}
              value={selectedToAccount}
              onChange={setSelectedToAccount}
              label="To Account *"
              placeholder="Search for account..."
            />
            <p className="text-xs text-gray-500 mt-1">{getHelpText()}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount (USD) *
            </label>
            <input
              type="number"
              name="amount"
              step="0.01"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Optional description or notes"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-[#FF6600] text-white rounded-md hover:bg-[#E55A00] disabled:bg-gray-400 transition-colors"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Transaction'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
